import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { ArrowLeft, MapPin, History } from "lucide-react";
import { SiteSelect } from "@/components/SiteSelect";

const OFFICE_VALUE = "__office__";
// Fix P (2026-09-09): datetime-local no admite segundos con "Z"; se recorta a minutos.
const nowLocalStr = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export default function SupervisorCheckin() {
  const [params] = useSearchParams();
  const preselect = params.get("client_id") || "";
  const [clients, setClients] = useState([]);
  const [active, setActive] = useState([]);
  const [clientId, setClientId] = useState(preselect);
  const [siteId, setSiteId] = useState("");
  const [siteCount, setSiteCount] = useState(0);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [retro, setRetro] = useState(false);
  const [retroIn, setRetroIn] = useState("");
  const [retroOut, setRetroOut] = useState("");

  const load = async () => {
    try {
      const [c, ck] = await Promise.all([api.get("/clients"), api.get("/supervisor-checkins?active_only=true")]);
      setClients(c.data); setActive(ck.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (preselect) setClientId(preselect); }, [preselect]);

  const resolvedPayload = () => (clientId === OFFICE_VALUE ? { client_id: null, is_office: true } : { client_id: clientId, is_office: false, site_id: siteId || undefined });

  const checkin = async () => {
    if (!clientId) return toast.error("Elegí un objetivo u Oficinas INVICT");
    if (clientId !== OFFICE_VALUE && siteCount > 0 && !siteId) return toast.error("Este objetivo tiene sedes cargadas: elegí una");
    setBusy(true);
    const send = (lat, lng) => api.post("/supervisor-checkins", { ...resolvedPayload(), latitude: lat, longitude: lng, notes: notes || undefined });
    try {
      const pos = await new Promise((res) => navigator.geolocation
        ? navigator.geolocation.getCurrentPosition(res, () => res(null), { timeout: 8000 })
        : res(null));
      await send(pos?.coords?.latitude ?? null, pos?.coords?.longitude ?? null);
      toast.success("Ingreso marcado"); setClientId(""); setSiteId(""); setNotes(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Fix P (2026-09-09): carga retroactiva -- visita que ya ocurrió (ej. ayer) y no
  // se pudo marcar en el momento. Se registra ingreso Y egreso manual, sin GPS.
  const submitRetro = async () => {
    if (!clientId) return toast.error("Elegí un objetivo u Oficinas INVICT");
    if (clientId !== OFFICE_VALUE && siteCount > 0 && !siteId) return toast.error("Este objetivo tiene sedes cargadas: elegí una");
    if (!retroIn || !retroOut) return toast.error("Indicá fecha/hora de ingreso y de egreso");
    if (new Date(retroIn) > new Date()) return toast.error("El ingreso no puede ser a futuro");
    if (new Date(retroOut) > new Date()) return toast.error("El egreso no puede ser a futuro");
    if (new Date(retroOut) < new Date(retroIn)) return toast.error("El egreso no puede ser anterior al ingreso");
    setBusy(true);
    try {
      const { data: opened } = await api.post("/supervisor-checkins", {
        ...resolvedPayload(), checkin_at: new Date(retroIn).toISOString(), notes: notes || undefined,
      });
      await api.post("/supervisor-checkins/checkout", { checkin_id: opened.id, checkout_at: new Date(retroOut).toISOString() });
      toast.success("Visita retroactiva registrada");
      setClientId(""); setSiteId(""); setNotes(""); setRetroIn(""); setRetroOut(""); setRetro(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const checkout = async (id) => {
    const outNotes = window.prompt("Observaciones de egreso (opcional, dejá vacío si no aplica):");
    if (outNotes === null) return; // el usuario canceló
    setBusy(true);
    try {
      const pos = await new Promise((res) => navigator.geolocation
        ? navigator.geolocation.getCurrentPosition(res, () => res(null), { timeout: 8000 })
        : res(null));
      await api.post("/supervisor-checkins/checkout", { checkin_id: id, latitude: pos?.coords?.latitude ?? null, longitude: pos?.coords?.longitude ?? null, notes: outNotes || undefined });
      toast.success("Egreso marcado"); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const nameOf = (c) => (c.is_office ? "Oficinas INVICT" : `${clients.find((cl) => cl.id === c.client_id)?.name || "—"}${c.site_name ? ` · ${c.site_name}` : ""}`);

  return (
    <div style={{ padding: "8px 16px" }}>
      <Link to="/supervisor" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Marcación de visita</h1>
      {/* Fix A (2026-08-28): el módulo se renombró a "Supervisores". */}
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>Marcá tu ingreso/egreso en el objetivo que estás recorriendo (o en Oficinas INVICT). Este check-in es lo que cuenta la parada como "efectuada" en Supervisores.</p>

      <div className="card" style={{ marginBottom: 18, padding: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A" }}>{retro ? "Cargar visita retroactiva" : "Marcar ingreso"}</div>
          <button type="button" onClick={() => setRetro((r) => !r)} className={retro ? "btn-primary" : "btn-ghost"} style={{ padding: "6px 10px", fontSize: 10 }} data-testid="checkin-retro-toggle">
            <History size={12} style={{ marginRight: 5 }}/>{retro ? "Volver a marcación en vivo" : "Ya ocurrió: cargar retroactiva"}
          </button>
        </div>

        <div className="field"><label>Objetivo</label>
          <select value={clientId} onChange={(e) => { setClientId(e.target.value); setSiteId(""); }} data-testid="checkin-client">
            <option value="">Elegir objetivo...</option>
            <option value={OFFICE_VALUE} data-testid="checkin-client-office">Oficinas INVICT</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {clientId && clientId !== OFFICE_VALUE && (
          <SiteSelect clientId={clientId} value={siteId} onChange={setSiteId} required testIdPrefix="checkin" onSitesLoaded={(sites) => setSiteCount(sites.length)} />
        )}

        {retro ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Ingreso *</label>
                <input type="datetime-local" value={retroIn} max={nowLocalStr()} onChange={(e) => setRetroIn(e.target.value)} data-testid="checkin-retro-in"/>
              </div>
              <div className="field"><label>Egreso *</label>
                <input type="datetime-local" value={retroOut} max={nowLocalStr()} onChange={(e) => setRetroOut(e.target.value)} data-testid="checkin-retro-out"/>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "-4px 0 10px" }}>Una carga retroactiva no lleva GPS: queda marcada como cargada a mano.</div>
          </>
        ) : null}

        <div className="field"><label>Observaciones (opcional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="Ej: no visité este cliente puntual, atendí un trámite de la empresa..."
            data-testid="checkin-notes" />
        </div>

        {retro ? (
          <button className="btn-primary" onClick={submitRetro} disabled={busy || !clientId || (clientId !== OFFICE_VALUE && siteCount > 0 && !siteId)} style={{ width: "100%", padding: 12 }} data-testid="checkin-retro-submit">
            <History size={14} style={{ marginRight: 6 }}/> Registrar visita retroactiva
          </button>
        ) : (
          <button className="btn-primary" onClick={checkin} disabled={busy || !clientId || (clientId !== OFFICE_VALUE && siteCount > 0 && !siteId)} style={{ width: "100%", padding: 12 }} data-testid="checkin-submit">
            <MapPin size={14} style={{ marginRight: 6 }}/> Marcar ingreso (con GPS)
          </button>
        )}
      </div>

      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Mis ingresos abiertos</div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {active.length === 0 && <div style={{ padding: 20, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin ingresos abiertos.</div>}
        {active.map((c) => (
          <div key={c.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }} data-testid={`checkin-row-${c.id}`}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}>
                {nameOf(c)}
                {c.is_backdated && <span className="badge amber" style={{ fontSize: 9 }} data-testid={`checkin-backdated-${c.id}`}>Retroactiva</span>}
              </div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Ingreso: {new Date(c.checkin_at).toLocaleString("es-AR")}</div>
              {c.notes_in && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2, fontStyle: "italic" }}>"{c.notes_in}"</div>}
            </div>
            <button onClick={() => checkout(c.id)} className="btn-primary" style={{ padding: "8px 14px", fontSize: 11 }} data-testid={`checkout-${c.id}`}>Egreso</button>
          </div>
        ))}
      </div>
    </div>
  );
}
